import React, { useState, useEffect, useRef } from 'react';
import styles from './File.module.scss';
import { USER_TYPE } from '../../../../../../utils/constants/user';
import { Mixpanel } from '../../../../../../utils/mixpanel';
import { getSubdomain } from '../../../../../../utils/utils';

import {
    downloadDarkGray,
    downloadIconDataRoom,
    downloadIconDataRoomHovered,
    fileIconDataRoom,
    PURPLE_THREE_DOTS_VERTICAL,
} from '../../../../../../assets/hostedassets';
import threeDotVertical from '../../../../../../assets/three-dot-vertical.svg';
import useFetchDataRoomFiles from '../../../../useFetchDataRoomFiles';
import FileViewerDrawer from '../../../../../../common/DocumentViewer/FileViewerDrawer';
import DocxDrawer from '../../../../../../common/DocumentViewer/DocxDrawer';
import { SheetEditorDrawer, isEditableSheetFile } from '../../../../../../common/SheetEditor';
import { classifyAddable } from '../../../../../../common/Reports/addFileToReports';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { useAppSelector } from '../../../../../../app/hooks';
import {
    AICA_DATAROOM_FILE_ARCHIVE,
    AICA_DATAROOM_FILE_DELETE,
    AICA_DATAROOM_FILE_DOWNLOAD,
    AICA_DATAROOM_FILE_MOVE,
} from '../../../../../../utils/constants/mixpanelEvents/investorEvents';
import { FILE_FOLDER_TYPES } from '../../../../../../store/investor/constants';
import { isLenderType } from '../../../../../../utils/utils';
import LoadingSpinner from '../../../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import type { FeatureVisibility } from '../../../../../../utils/visibilityConfigUtils';

const File = ({
    name,
    lastUpdated,
    index,
    docId,
    masterFolderName,
    fetchFiles,
    setShowDataRoomModal,
    isNew,
    startDate,
    endDate,
    selectedItems = new Set(),
    onItemSelect,
    showCheckboxes = false,
    dataRoomDownloadVisibility,
    addToReportsVisible,
}: {
    name: string;
    lastUpdated: string;
    index: number;
    docId?: any;
    masterFolderName: string;
    fetchFiles: any;
    setShowDataRoomModal: any;
    isNew?: boolean;
    startDate?: string;
    endDate?: string;
    selectedItems?: Set<string>;
    onItemSelect?: (itemId: string) => void;
    showCheckboxes?: boolean;
    dataRoomDownloadVisibility: FeatureVisibility;
    addToReportsVisible: boolean;
}) => {
    const [downloadImgUrl, setDownloadImgUrl] = useState(downloadIconDataRoomHovered);
    const [showOtherOptions, setShowOtherOptions] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });

    const dropdownRef: any = useRef(null);
    const threeDotsRef: any = useRef(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const fileViewType = searchParams.get('fileViewType');
    const isLender = isLenderType();
    const isArchiveView = fileViewType === 'archived';

    const {
        handleFileClick,
        showFile,
        setShowFile,
        showSheetEditor,
        setShowSheetEditor,
        showDocxDrawer,
        setShowDocxDrawer,
        handleEditSheet,
        fileUrl,
        fileMimeType,
        downloadTheFile,
        handleDeleteFile,
        handleUnarchiveFileBulk,
        getFormattedDate,
        downloadingFile,
        MoveToActive,
        DeleteButton,
        MoveButton,
        ArchiveButton,
        AddToReportsButton,
        handleAddToReports,
        addingToReports,
        addedToReports,
    } = useFetchDataRoomFiles(fetchFiles);

    const canEditSheet = isEditableSheetFile(name);
    // S3-backed docIds (non-numeric) can't be snapshotted via handleAddToReports's
    // presigned-URL lookup — hide the affordance rather than showing an action
    // that errors on click.
    const canAddToReports = addToReportsVisible && classifyAddable(name) !== null && !isNaN(Number(docId));
    // Primary header action on the Sheet/Docx drawers — same gate + org context
    // as the three-dots "Add to Reports" entry point (both coexist), plus the
    // archive-view gate (three-dots already hides it there via canAddToReports
    // being checked inside the non-archive dropdown branch only).
    const onDrawerAddToReports = docId && canAddToReports && !isArchiveView ? () => handleAddToReports(docId, name) : undefined;

    const user = useAppSelector((state) => state.user.userData);
    const selectedCompany = useAppSelector((state) => state.investor.selectedCompany);
    const isPartner = user?.accountType === USER_TYPE.PARTNER;
    const isLenderOrPartner = isLender || isPartner;

    const fileId = docId ? String(docId) : name;
    const isSelected = selectedItems.has(fileId);

    const calculateDropdownPosition = () => {
        if (threeDotsRef.current) {
            //@ts-ignore
            const rect = threeDotsRef.current.getBoundingClientRect();
            // Right-anchor the menu to the three-dots button so it grows leftward and
            // never clips the (wider) "Add to Reports" label off the right viewport edge.
            setDropdownPosition({
                top: rect.bottom + window.scrollY,
                right: Math.max(window.innerWidth - rect.right, 8),
            });
        }
    };

    useEffect(() => {
        if (showOtherOptions) {
            calculateDropdownPosition();
        }
    }, [showOtherOptions]);

    useEffect(() => {
        const handleScroll = () => {
            if (showOtherOptions) {
                setShowOtherOptions(false);
            }
        };
        window.addEventListener('scroll', handleScroll, true);
        return () => {
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [showOtherOptions]);

    // Handle outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target) &&
                threeDotsRef.current &&
                !threeDotsRef.current?.contains(event.target)
            ) {
                setShowOtherOptions(false);
            }
        };
        if (showOtherOptions) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showOtherOptions]);

    const dropdown = showOtherOptions
        ? createPortal(
              <div
                  ref={dropdownRef}
                  className={styles.OtherOptions}
                  style={{ top: dropdownPosition.top, right: dropdownPosition.right }}
                  onClick={(e) => e.stopPropagation()}
              >
                  {isArchiveView ? (
                      <>
                          <MoveToActive
                              docId={docId}
                              masterFolderName={masterFolderName}
                              fetchFiles={fetchFiles}
                              setShowOtherOptions={setShowOtherOptions}
                              user={user}
                              selectedCompany={selectedCompany}
                              handleUnarchiveFileBulk={handleUnarchiveFileBulk}
                          />
                          <DeleteButton
                              docId={docId}
                              name={name}
                              masterFolderName={masterFolderName}
                              fetchFiles={fetchFiles}
                              setShowOtherOptions={setShowOtherOptions}
                              user={user}
                              selectedCompany={selectedCompany}
                              handleDeleteFile={handleDeleteFile}
                              isArchive={false}
                          />
                      </>
                  ) : (
                      <>
                          <MoveButton
                              docId={docId}
                              masterFolderName={masterFolderName}
                              setShowOtherOptions={setShowOtherOptions}
                              setShowDataRoomModal={setShowDataRoomModal}
                              user={user}
                              selectedCompany={selectedCompany}
                          />
                          {canAddToReports && (
                              <AddToReportsButton
                                  docId={docId}
                                  name={name}
                                  setShowOtherOptions={setShowOtherOptions}
                                  handleAddToReports={handleAddToReports}
                              />
                          )}
                          <DeleteButton
                              docId={docId}
                              name={name}
                              masterFolderName={masterFolderName}
                              fetchFiles={fetchFiles}
                              setShowOtherOptions={setShowOtherOptions}
                              user={user}
                              selectedCompany={selectedCompany}
                              handleDeleteFile={handleDeleteFile}
                              isArchive={false}
                          />
                          <ArchiveButton
                              docId={docId}
                              name={name}
                              masterFolderName={masterFolderName}
                              fetchFiles={fetchFiles}
                              setShowOtherOptions={setShowOtherOptions}
                              user={user}
                              selectedCompany={selectedCompany}
                              handleDeleteFile={handleDeleteFile}
                          />
                      </>
                  )}
              </div>,
              document.body,
          )
        : null;

    const handleCheckboxClick = (e: React.MouseEvent) => {
        e.stopPropagation();
    };

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation();
        if (onItemSelect) {
            onItemSelect(fileId);
        }
    };

    return (
        <>
            {/* The whole row opens the file — checkbox / three-dots / dropdown / download
                all stop propagation so they don't also trigger the open handler. The
                drawers below are deliberately rendered OUTSIDE this div: they portal via
                BottomDrawer/SideDrawer, but stay in the same React tree, so a click inside
                an open drawer would otherwise bubble up to this onClick and re-open/refetch
                the file mid-edit. */}
            <div
                className={isLenderOrPartner ? styles.fileLenderView : styles.file}
                onClick={async () => {
                    if (!docId) return;
                    if (canEditSheet) await handleEditSheet(docId, name);
                    else await handleFileClick(docId, name);
                }}
            >
                <div className={styles.nameCell}>
                    <div
                        style={{
                            marginLeft: `${index * 1.25}rem`,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                        }}
                    >
                        {showCheckboxes && (
                            <>
                                <span style={{ width: '0.75rem', display: 'inline-block' }}></span>
                                <div className={styles.checkboxCell} onClick={handleCheckboxClick}>
                                    <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={handleCheckboxChange}
                                        className={styles.checkbox}
                                    />
                                </div>
                            </>
                        )}
                        <span className={styles.FileIconContainer}>
                            <img src={fileIconDataRoom} alt={'folder'} />
                        </span>
                        <span className={styles.FileName} title={name} aria-label={name}>
                            {name}
                        </span>
                    </div>
                </div>
                <span className={isNew ? styles.labelStatus : ''}>{isNew ? 'New' : ''}</span>
                <span className={startDate && styles.DateContainer}>{getFormattedDate('file', startDate ?? '')}</span>
                <span className={endDate && styles.DateContainer}>{getFormattedDate('file', endDate ?? '')}</span>
                <span className={styles.FileDate}>{lastUpdated}</span>
                {!isLenderOrPartner && dataRoomDownloadVisibility.show && (
                    <span
                        onClick={(e) => {
                            e.stopPropagation();
                            if (!dataRoomDownloadVisibility.write) return;
                            if (docId) downloadTheFile(docId, name);

                            Mixpanel.track(AICA_DATAROOM_FILE_DOWNLOAD, {
                                userRole: user?.accountType,
                                pnoPlatform: getSubdomain(),
                                userId: user?.userId,
                                currentUrl: window.location.href,
                                organizationId: selectedCompany?.assesseeOrgId,
                                fileType: masterFolderName,
                            });
                        }}
                        className={styles.downloadIconContainer}
                        style={dataRoomDownloadVisibility.hidden ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                        onMouseEnter={() => dataRoomDownloadVisibility.write && setDownloadImgUrl(downloadIconDataRoomHovered)}
                        onMouseLeave={() => dataRoomDownloadVisibility.write && setDownloadImgUrl(downloadIconDataRoomHovered)}
                    >
                        {downloadingFile ? (
                            <LoadingSpinner color="#6021B3" style={{ margin: '0', padding: '0' }} />
                        ) : (
                            <img src={downloadImgUrl} alt={'icon'} />
                        )}
                    </span>
                )}
                {/* {!isLenderOrPartner && <div className={styles.separator} />} */}
                {!isLenderOrPartner && (
                    <span
                        ref={threeDotsRef}
                        className={styles.ThreeDots}
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowOtherOptions(!showOtherOptions);
                        }}
                    >
                        <img src={threeDotVertical} alt={'More'} />
                    </span>
                )}
                {dropdown}
            </div>
            {showFile && (
                <FileViewerDrawer
                    heading={name}
                    fileUrl={fileUrl}
                    mimeType={fileMimeType}
                    downloading={downloadingFile}
                    onDownload={() => {
                        if (docId) downloadTheFile(docId, name);
                    }}
                    onClose={() => setShowFile(false)}
                />
            )}
            {showSheetEditor && (
                <SheetEditorDrawer
                    open={showSheetEditor}
                    fileUrl={fileUrl}
                    mimeType={fileMimeType}
                    fileName={name}
                    onClose={() => setShowSheetEditor(false)}
                    onAddToReports={onDrawerAddToReports}
                    addingToReports={addingToReports}
                    addedToReports={addedToReports}
                />
            )}
            {showDocxDrawer && (
                <DocxDrawer
                    open={showDocxDrawer}
                    fileName={name}
                    fileUrl={fileUrl}
                    onClose={() => setShowDocxDrawer(false)}
                    onDownload={() => {
                        if (docId) downloadTheFile(docId, name);
                    }}
                    downloading={downloadingFile}
                    onAddToReports={onDrawerAddToReports}
                    addingToReports={addingToReports}
                    addedToReports={addedToReports}
                />
            )}
        </>
    );
};

export default File;
